/*
 * Copyright © 2021-present Protonbase Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Protonbase Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Protonbase Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { Space, TableColumnsType } from 'antd'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { ModelStatement } from 'account-client'
import { StateTag } from '@/components/StateTag'
import { useTranslateAmount } from '@/hooks/useTranslateAmount'
import { ConfirmStatement } from '@/pages/billing/features/ConfirmStatement'
import { DownloadStatement } from '@/pages/billing/features/DownloadStatement'
import { StatementConfirmState } from '@/pages/billing/interface'
import { timeFormatter } from '@/utils/timeFormatter'

export function useStatementsColumns() {
  const { t } = useTranslation()
  const { contractId } = useParams()
  const { currency } = useTranslateAmount()

  const columns = useMemo<TableColumnsType<ModelStatement>>(
    () => [
      {
        title: t('Statement Time'),
        fixed: 'left',
        dataIndex: 'start',
        render: (value, record) => (
          <Link to={`/billing-center/${contractId}/statements/${record.id}`}>{timeFormatter.format(value, 'yearMonth')}</Link>
        ),
      },
      {
        title: `${t('Consumption Amount')}（${currency}）`,
        dataIndex: 'amount',
        sorter: true,
        render: (_, record) => record.amountFormatted,
      },
      {
        title: `${t('Payable Amount')}（${currency}）`,
        dataIndex: 'payableAmount',
        sorter: true,
        render: (_, record) => record.payableAmountFormatted,
      },
      {
        title: `${t('Vouchers Redeemed')}（${currency}）`,
        dataIndex: 'voucherDeductedFormatted',
      },
      {
        title: t('Due Date'),
        dataIndex: 'dueDate',
        render: value => timeFormatter.format(value, 'date'),
      },
      {
        title: `${t('Deducted Amount')}（${currency}）`,
        dataIndex: 'cashDeductedFormatted',
      },
      {
        title: `${t('Undeducted Amount')}（${currency}）`,
        dataIndex: 'undeductedAmountFormatted',
      },
      {
        title: t('State'),
        render: (record: ModelStatement) => (
          <StateTag state={record.confirmState === StatementConfirmState.confirmed ? record.deductState : record.confirmState} />
        ),
      },
      {
        title: t('Actions'),
        dataIndex: 'id',
        width: 180,
        render: (value: string, record: ModelStatement) => (
          <Space>
            <ConfirmStatement isConfirmed={record.confirmState === StatementConfirmState.confirmed} statementId={record.id} size="small" />
            <DownloadStatement
              disabled={record.confirmState !== StatementConfirmState.confirmed}
              statementId={value}
              text={t('Download')}
              size="small"
            />
          </Space>
        ),
      },
    ],
    [],
  )

  return columns
}
